import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const PermissionsContext = createContext();

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [permissionsMapping, setPermissionsMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("accessToken"));
  const [loginUserID, setLoginUserID] = useState(
    JSON.parse(localStorage.getItem("admin"))?.data?.id || null
  );
  const location = useLocation();

  // A user holds several roles at once, so "the permissions of role X" is no
  // longer a question the client can ask: it would have to pick one role and
  // silently drop the grants of the others. admin/me/permissions answers for
  // the whole session, merged server side, which is also what makes the result
  // deterministic — the same session resolves the same set on every reload
  // instead of depending on which role the legacy lookup happened to return.
  const fetchSessionPermissionIds = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API}admin/me/permissions`,
      { headers: { "x-access-token": token } }
    );
    // The endpoint answers { status, data: { roles, primary_role,
  // permission_ids, permissions } }. Reading the top level found nothing,
  // the non-array branch below treated that as a legacy API, and the whole
  // console silently fell back to the single-role path: the multi-role
  // union this endpoint exists to provide was never used.
  const body = response?.data;
  const ids = body?.data?.permission_ids ?? body?.permission_ids;
    // The matrix is keyed by numeric permission id (see permissionUtils).
    // A response without that array is an older API answering a path it does
    // not implement, so treat it exactly like a 404 and take the legacy route.
    return Array.isArray(ids) ? ids : null;
  };

  // Legacy two-call path, kept for consoles running against an API that
  // predates admin/me/permissions: resolve the session's single roleId, then
  // read that role's permissions.
  const fetchLegacyRoleId = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}get-user-details-byid`,
        {
          user_id: JSON.parse(localStorage.getItem("admin"))?.data?.id || null,
        },
        {
          headers: {
            "x-access-token": localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );
      return response?.data?.data?.roleId;
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  };

  const fetchLegacyPermissionIds = async () => {
    const roleId = await fetchLegacyRoleId();
    if (!roleId) return null;
    const response = await axios.get(
      `${process.env.REACT_APP_API}admin/roles/${roleId}/permissions`,
      { headers: { "x-access-token": token } }
    );
    return response?.data?.permissions || [];
  };

  const fetchMapping = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API}admin/permissions-mapping`,
      { headers: { "x-access-token": token } }
    );
    return response?.data?.permissions || {};
  };

  const fetchPermissionsData = async () => {
    try {
      let ids = null;
      try {
        ids = await fetchSessionPermissionIds();
      } catch (error) {
        // Only a missing endpoint means "this API is older than the session
        // endpoint". Any other failure (401, 500) is a real error: falling
        // back would just ask a second question of an API that is already
        // failing, and could resolve a different set on the next reload.
        if (error?.response?.status !== 404) throw error;
      }
      if (ids === null) {
        ids = await fetchLegacyPermissionIds();
      }

      const mapping = await fetchMapping();
      setPermissions(ids || []);
      setPermissionsMapping(mapping);
    } catch (error) {
      console.error("Error fetching permissions data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (token && loginUserID) {
        setLoading(true);
        await fetchPermissionsData();
      } else {
        clearPermissions();
        setLoading(false);
      }
    };

    fetchData();
  }, [token, loginUserID]);

  const syncAuthState = useCallback(() => {
    const currentToken = localStorage.getItem("accessToken");
    const userData = JSON.parse(localStorage.getItem("admin"));
    const userID = userData?.data?.id || null;
    if (currentToken !== token || userID !== loginUserID) {
      setToken(currentToken);
      setLoginUserID(userID);
    }
  }, [token, loginUserID]);

  useEffect(() => {
    syncAuthState();
  }, [location, syncAuthState]);

  useEffect(() => {
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("focus", syncAuthState);
    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("focus", syncAuthState);
    };
  }, [syncAuthState]);

  const clearPermissions = () => {
    setPermissions([]);
    setPermissionsMapping({});
  };

  return (
    <PermissionsContext.Provider
      value={{ permissions, permissionsMapping, loading, clearPermissions }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
